import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getAppendixImages,
  uploadAppendixImage,
  fetchAppendixImageBlob,
  updateAppendixCaption,
  updateAppendixPdfSize,
  deleteAppendixImage,
} from '../utils/storage';
import LazyBlobImage from './LazyBlobImage';
import { useReadOnly } from '../contexts/ReadOnlyContext';
import './AppendixCard.css';

// PDF-size cycle order. Each click on the size toggle advances to the
// next, wrapping around. Labels match exactly what shows on the button.
const PDF_SIZE_CYCLE = ['normal', 'small', 'smaller'];
const PDF_SIZE_LABELS = {
  normal:  'Normal',
  small:   'Small',
  smaller: 'Smaller',
};

/* ── Title mapping by inspection type ─────────────────────────── */
const APPENDIX_TITLES = {
  'VT-OSI': 'Visual Inspection Appendix',
  'VT-ISI': 'Visual Inspection Appendix',
  'VT':     'Visual Inspection Appendix',
  'UT-OSI': 'Ultrasonic Inspection Appendix',
  'UT-ISI': 'Ultrasonic Inspection Appendix',
  'UT':     'Ultrasonic Inspection Appendix',
  'MT-OSI': 'Magnetic Inspection Appendix',
  'MT-ISI': 'Magnetic Inspection Appendix',
  'MT':     'Magnetic Inspection Appendix',
  'PT-OSI': 'Penetrant Inspection Appendix',
  'PT-ISI': 'Penetrant Inspection Appendix',
  'PT':     'Penetrant Inspection Appendix',
  'DEFO':   'Deformation Measurement Appendix',
  'CW-OSI': 'Corner-weld Inspection Appendix',
};

/* ── Single image card ────────────────────────────────────────── */
function AppendixImageCard({ image, onDelete, onCaptionUpdate, onPdfSizeUpdate, referencedIn = [] }) {
  const readOnly = useReadOnly();   // archived → view images only (no delete/size/caption edit)
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(image.caption || '');
  const inputRef = useRef(null);
  const pdfSize = PDF_SIZE_CYCLE.includes(image.pdf_size) ? image.pdf_size : 'normal';

  const fetchBlob = useCallback(() => fetchAppendixImageBlob(image.id), [image.id]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const handleCaptionBlur = useCallback(() => {
    setEditing(false);
    if (caption !== (image.caption || '')) {
      onCaptionUpdate(image.id, caption);
    }
  }, [caption, image.caption, image.id, onCaptionUpdate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handlePdfSizeClick = () => {
    const idx = PDF_SIZE_CYCLE.indexOf(pdfSize);
    const next = PDF_SIZE_CYCLE[(idx + 1) % PDF_SIZE_CYCLE.length];
    onPdfSizeUpdate(image.id, next);
  };

  return (
    <div className="cg-card appendix-card">
      <div className="cg-image-wrapper">
        <LazyBlobImage
          fetchBlob={fetchBlob}
          alt={caption || 'Appendix image'}
          className="cg-image"
        />
        {/* Red "Delete" label badge (top-left, hover-revealed). Shared
            with .gim-photo-delete on the object-photo thumbnail in the
            assessment modal, which is why the previous ×-icon variant
            broke that affordance silently. */}
        {!readOnly && (
          <button
            type="button"
            className="appendix-delete-label"
            onClick={() => onDelete(image.id)}
            title="Delete image"
          >
            Delete
          </button>
        )}
        {/* PDF render-size toggle, always visible on the right side
            so the user knows the current size at a glance. */}
        {!readOnly && (
          <button
            type="button"
            className={`appendix-pdf-size-btn appendix-pdf-size-${pdfSize}`}
            onClick={handlePdfSizeClick}
            title="Click to cycle PDF size (Normal → Small → Smaller)"
          >
            {PDF_SIZE_LABELS[pdfSize]}
          </button>
        )}
        {referencedIn.length > 0 && (
          <div className="appendix-ref-badge" title={`Used as a reference image in: ${referencedIn.join('; ')}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="1.5" />
              <circle cx="8" cy="10" r="1.5" />
              <polyline points="5 17 10 12 14 16 18 12 21 15" />
            </svg>
          </div>
        )}
      </div>
      <div className="cg-caption">
        {editing && !readOnly ? (
          <input
            ref={inputRef}
            className="appendix-caption-input"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            onBlur={handleCaptionBlur}
            onKeyDown={handleKeyDown}
            placeholder="Add a caption..."
          />
        ) : (
          <span
            className="appendix-caption-text"
            onClick={readOnly ? undefined : () => setEditing(true)}
          >
            {caption || (!readOnly && <span className="appendix-caption-placeholder">Click to add caption</span>)}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Main AppendixCard component ──────────────────────────────── */
// `referencedBy` maps appendix image id → the places it's used as a reference
// image (e.g. ["List of annotations", "Measurement table: Shell Course"]);
// a non-empty entry shows the reference badge on that thumbnail.
export default function AppendixCard({ inspectionId, inspectionType, referencedBy = {}, onImageDeleted }) {
  const readOnly = useReadOnly();   // archived → no upload dropzone
  const [isOpen, setIsOpen] = useState(() => {
    const saved = sessionStorage.getItem(`tib_appendix_open_${inspectionId}`);
    return saved === null ? true : saved === 'true';
  });
  const [images, setImages] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const title = APPENDIX_TITLES[inspectionType] || 'Inspection Appendix';

  // Load images on mount
  useEffect(() => {
    getAppendixImages(inspectionId)
      .then(setImages)
      .catch((err) => console.error('Failed to load appendix images:', err));
  }, [inspectionId]);

  // Persist collapse state
  useEffect(() => {
    sessionStorage.setItem(`tib_appendix_open_${inspectionId}`, String(isOpen));
  }, [isOpen, inspectionId]);

  const handleFiles = useCallback(async (files) => {
    const valid = Array.from(files).filter(
      (f) => f.type === 'image/jpeg' || f.type === 'image/png'
    );
    if (valid.length === 0) return;

    setUploading(true);
    for (const file of valid) {
      try {
        const newImage = await uploadAppendixImage(inspectionId, file);
        setImages((prev) => [...prev, newImage]);
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    setUploading(false);
  }, [inspectionId]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleCaptionUpdate = useCallback(async (imageId, newCaption) => {
    try {
      await updateAppendixCaption(imageId, newCaption);
      setImages((prev) =>
        prev.map((img) => (img.id === imageId ? { ...img, caption: newCaption } : img))
      );
    } catch (err) {
      console.error('Caption update failed:', err);
    }
  }, []);

  const handlePdfSizeUpdate = useCallback(async (imageId, newSize) => {
    // Optimistic toggle — the toggle is a single click, so the user
    // gets instant feedback. If the API call fails we roll back.
    setImages((prev) =>
      prev.map((img) => (img.id === imageId ? { ...img, pdf_size: newSize } : img))
    );
    try {
      await updateAppendixPdfSize(imageId, newSize);
    } catch (err) {
      console.error('PDF size update failed:', err);
      // Reload from server so the UI matches the persisted state.
      getAppendixImages(inspectionId).then(setImages).catch(() => {});
    }
  }, [inspectionId]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const deletedId = deleteTarget;
    try {
      await deleteAppendixImage(deletedId);
      setImages((prev) => prev.filter((img) => img.id !== deletedId));
      // Backend already scrubs the now-dead id from
      // synopsis.annotationReferenceIds and ut_data.tables[].referenceImageIds,
      // but the parent page still has the OLD inspection in state. Tell
      // it so it can re-fetch and the "Reference Images (N)" badges
      // collapse without a manual page reload.
      onImageDeleted?.(deletedId);
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setDeleteTarget(null);
  }, [deleteTarget, onImageDeleted]);

  return (
    <div className="insp-appendix-card">
      {/* Header — clickable collapse */}
      <div className="insp-section-title-row" onClick={() => setIsOpen((o) => !o)}>
        <div className="insp-appendix-title">{title}</div>
        <svg className={`insp-collapse-chevron ${isOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </div>

      {/* Collapsible body */}
      {isOpen && <div className="insp-appendix-body">
        {/* Drop zone — hidden in read-only (archived) */}
        {!readOnly && (
        <div
          className={`appendix-dropzone${dragOver ? ' drag-over' : ''}${uploading ? ' uploading' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {uploading ? 'Uploading...' : 'Drag and drop images here, or click to browse'}
          <div className="appendix-dropzone-hint">JPG, JPEG, PNG only (max 10 MB)</div>
        </div>
        )}

        {/* Empty note (read-only archived view has no dropzone) */}
        {readOnly && images.length === 0 && (
          <div className="appendix-empty-note">No images were uploaded</div>
        )}

        {/* Image grid */}
        {images.length > 0 && (
          <div className="cg-grid">
            {images.map((img) => (
              <AppendixImageCard
                key={img.id}
                image={img}
                onDelete={setDeleteTarget}
                onCaptionUpdate={handleCaptionUpdate}
                onPdfSizeUpdate={handlePdfSizeUpdate}
                referencedIn={referencedBy[img.id] || []}
              />
            ))}
          </div>
        )}
      </div>}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Delete Image?</h2>
            <p className="modal-body-text">
              This image will be permanently removed from the appendix.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
